import React from 'react'
import type { GeoJSON } from 'geojson'
import { MapboxMap, type MapViewport } from '@/modules/routes/components'
import { useMapDataContext } from '@/modules/routes/contexts/MapDataContext'
import { useMapPageContext } from '@/modules/routes/contexts/MapPageContext'

interface MapContainerProps {
  poiResults?: any[]
  searchResult?: { id: string; name: string; latitude: number; longitude: number; category?: string; poi_type?: string; address?: string } | null
  deadEnds?: any[]
  selectedDeadEnd?: any
  searchRouteGeometry?: GeoJSON.LineString | GeoJSON.MultiLineString | null
  searchRadiusMiles?: number | null
  searchBboxVisualization?: {
    bbox: [number, number, number, number]
    bboxArea?: number
    bboxSize?: { width: number; height: number }
    bboxCenter?: [number, number]
    searchCenter?: [number, number]
    searchRadiusMiles?: number
  } | null
  onRouteClick?: (route: any) => void
  onWaypointClick?: (waypoint: any) => void
  onPOIClick?: (poi: any) => void
  onDeadEndClick?: (deadEnd: any) => void
  onMapClick?: (coords: any) => void
  onSaveMapClickPoint?: () => void
  mapClickCoordinates?: { latitude: number; longitude: number } | null
  isDeadEndDiscoveryMode?: boolean
  getAccessToken?: () => Promise<string | null>
  centerOnSearch?: { latitude: number; longitude: number } | null
  centerOnUserLocation?: { latitude: number; longitude: number } | null
  centerOnWaypoint?: any
  centerOnWaypoints?: Array<{ latitude?: number | null; longitude?: number | null }> | null
  centerOnPOI?: { latitude: number; longitude: number } | null
  centerOnDeadEnd?: { latitude: number; longitude: number } | null
  userLocation?: any
  mode?: 'browse' | 'build'
  isDrawMode?: boolean
  drawingPoints?: [number, number][]
  snappedGeometry?: GeoJSON.LineString | null
  currentHoverPoint?: [number, number] | null
  hoverPreviewGeometry?: GeoJSON.LineString | null
  isSnapping?: boolean
  isHoverSnapping?: boolean
  onDrawingPointAdd?: (coords: [number, number]) => void
  onDrawingHover?: (coords: [number, number] | null) => void
  onViewportChange?: (viewport: MapViewport) => void
  selectedTripId?: string | null // Trip ID for itinerary waypoint markers
  trip?: any | null // Trip data for itinerary builder
  isItineraryBuilder?: boolean // Whether this is the itinerary builder
  selectedWaypoint?: any | null // Override selectedWaypoint from context (for itinerary builder)
  showLibraryWaypoints?: boolean // Toggle to show/hide library waypoints on map
  showPublicLands?: boolean // Toggle to show/hide public land boundaries
  onPublicLandsLoadingChange?: (loading: boolean) => void // Callback for public lands loading state changes
  showDispersedCampsites?: boolean // Toggle to show/hide dispersed camp finder layer
  onDispersedCampsitesLoadingChange?: (loading: boolean) => void // Callback for camp finder loading state
  onDispersedCampsitesUpgradeRequired?: () => void // Called when the campsite API returns 403 (Pro required)
  onForestRoadsError?: (message: string | null) => void // Callback when forest-roads request fails (e.g. zoom in)
}

/**
 * Container component for MapboxMap
 * Uses MapDataContext for stable waypoints/routes data
 * Uses MapPageContext for panel state (selectedRoute, selectedWaypoint)
 * Map persists across panel changes
 */
export function MapContainer({
  poiResults = [],
  searchResult = null,
  deadEnds = [],
  selectedDeadEnd,
  searchRouteGeometry = null,
  searchRadiusMiles = null,
  searchBboxVisualization = null,
  onRouteClick,
  onWaypointClick,
  onPOIClick,
  onDeadEndClick,
  onMapClick,
  onSaveMapClickPoint,
  mapClickCoordinates = null,
  isDeadEndDiscoveryMode = false,
  getAccessToken,
  centerOnSearch = null,
  centerOnUserLocation = null,
  centerOnWaypoint = null,
  centerOnWaypoints = null,
  centerOnPOI = null,
  centerOnDeadEnd = null,
  userLocation,
  mode = 'browse',
  isDrawMode,
  drawingPoints,
  snappedGeometry,
  currentHoverPoint,
  hoverPreviewGeometry,
  isSnapping,
  isHoverSnapping,
  onDrawingPointAdd,
  onDrawingHover,
  onViewportChange,
  selectedTripId = null,
  trip = null,
  isItineraryBuilder = false,
  selectedWaypoint: selectedWaypointProp = undefined,
  showLibraryWaypoints = false,
  showPublicLands = false,
  onPublicLandsLoadingChange,
  showDispersedCampsites = false,
  onDispersedCampsitesLoadingChange,
  onDispersedCampsitesUpgradeRequired,
  onForestRoadsError
}: MapContainerProps) {
  // Get stable data from context
  const { waypoints, routes } = useMapDataContext()
  
  // Get panel state from context (for highlighting selected items)
  const { selectedRoute, selectedWaypoint: selectedWaypointFromContext, selectedPOI } = useMapPageContext()
  
  // Use prop if provided (for itinerary builder), otherwise use context
  const selectedWaypoint = selectedWaypointProp !== undefined ? selectedWaypointProp : selectedWaypointFromContext

  return (
    <MapboxMap
      routes={routes}
      waypoints={waypoints}
      poiResults={poiResults}
      searchResult={searchResult}
      selectedRoute={selectedRoute}
      selectedPOI={selectedPOI}
      selectedWaypoint={selectedWaypoint}
      onRouteClick={onRouteClick}
      onWaypointClick={onWaypointClick}
      onPOIClick={onPOIClick}
      onDeadEndClick={onDeadEndClick}
      deadEnds={deadEnds}
      selectedDeadEnd={selectedDeadEnd}
      searchRouteGeometry={searchRouteGeometry}
      searchRadiusMiles={searchRadiusMiles}
      searchBboxVisualization={searchBboxVisualization}
      onMapClick={onMapClick}
      height="100%"
      className="w-full h-full"
      isDrawMode={isDrawMode ?? mode === 'build'}
      drawingPoints={drawingPoints}
      snappedGeometry={snappedGeometry}
      currentHoverPoint={currentHoverPoint}
      hoverPreviewGeometry={hoverPreviewGeometry}
      isSnapping={isSnapping}
      isHoverSnapping={isHoverSnapping}
      onDrawingPointAdd={onDrawingPointAdd}
      onDrawingHover={onDrawingHover}
      centerOnRoute={selectedRoute}
      centerOnWaypoint={centerOnWaypoint}
      centerOnWaypoints={centerOnWaypoints}
      centerOnSearch={centerOnSearch}
      centerOnUserLocation={centerOnUserLocation}
      centerOnPOI={centerOnPOI}
      centerOnDeadEnd={centerOnDeadEnd}
      mapClickCoordinates={mapClickCoordinates}
      isDeadEndDiscoveryMode={isDeadEndDiscoveryMode}
      getAccessToken={getAccessToken}
      onSaveMapClickPoint={onSaveMapClickPoint}
      onViewportChange={onViewportChange}
      selectedTripId={selectedTripId}
      trip={trip}
      isItineraryBuilder={isItineraryBuilder}
      showLibraryWaypoints={showLibraryWaypoints}
      showPublicLands={showPublicLands}
      onPublicLandsLoadingChange={onPublicLandsLoadingChange}
      showDispersedCampsites={showDispersedCampsites}
      onDispersedCampsitesLoadingChange={onDispersedCampsitesLoadingChange}
      onDispersedCampsitesUpgradeRequired={onDispersedCampsitesUpgradeRequired}
      onForestRoadsError={onForestRoadsError}
    />
  )
}

